import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Resolves a custom slug (scorz.co.uk/<slug>) into a competition.
 *  - If the competition is OPEN → deep-link to landing page with that event
 *    pre-opened (/?event=<id>).
 *  - If the competition is CLOSED ("Coming Soon" toggle off) → render the
 *    branded "Entries opening soon" splash with the date if one was set.
 *  - If the slug doesn't match anything → bounce to the landing page.
 */
export default function SlugRedirect() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', comp: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/competition-by-slug/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        const comp = res?.data;
        if (!comp?.id) {
          setState({ status: 'notfound', comp: null });
          setTimeout(() => navigate('/', { replace: true }), 1500);
          return;
        }
        if (comp.isOpen) {
          navigate(`/?event=${comp.id}`, { replace: true });
        } else {
          setState({ status: 'soon', comp });
        }
      } catch {
        if (!cancelled) {
          setState({ status: 'notfound', comp: null });
          setTimeout(() => navigate('/', { replace: true }), 1500);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  // Loading shell
  if (state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center" data-testid="slug-redirect">
          <div className="w-10 h-10 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading competition…</p>
        </div>
      </div>
    );
  }

  // Not found
  if (state.status === 'notfound') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center" data-testid="slug-not-found">
          <img src="/scorz-logo.png" alt="Scorz" className="h-12 mx-auto mb-6" />
          <p className="text-lg font-semibold text-gray-900">Competition not found</p>
          <p className="text-sm text-gray-500 mt-1">Redirecting…</p>
        </div>
      </div>
    );
  }

  // "Coming Soon" splash
  const { comp } = state;
  const dateLine = formatEntriesOpenLine(comp);
  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="text-center max-w-xl" data-testid="entries-coming-soon">
        <img src="/scorz-logo.png" alt="Scorz" className="h-16 w-auto mx-auto mb-8" />
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
          {comp.title}
        </h1>
        <p className="mt-6 text-lg text-gray-700">
          {comp.entriesOpenSoonMessage || 'Entries opening soon'}
        </p>
        {dateLine && (
          <p className="mt-2 text-base text-gray-500" data-testid="entries-open-date">
            {dateLine}
          </p>
        )}
        {comp.publicDates && (
          <p className="mt-6 text-sm text-gray-400">
            Competition date: <span className="text-gray-600">{comp.publicDates}</span>
          </p>
        )}
        <div className="mt-10">
          <button
            onClick={() => navigate('/', { replace: true })}
            className="text-sm font-medium text-gray-600 hover:text-black underline underline-offset-4"
            data-testid="back-to-scorz"
          >
            Back to scorz.co.uk
          </button>
        </div>
      </div>
    </div>
  );
}

function formatEntriesOpenLine(comp) {
  const { entryStartDate, entryStartTime } = comp || {};
  if (!entryStartDate) return null;

  // entryStartDate is ISO YYYY-MM-DD. Render in a friendly format without
  // any time-zone surprises (we never new Date(...) the raw string).
  const [y, m, d] = entryStartDate.split('-');
  if (!y || !m || !d) return null;
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const day = parseInt(d, 10);
  const month = months[parseInt(m, 10) - 1] || '';
  const yr = parseInt(y, 10);
  const dateStr = `${day} ${month} ${yr}`;

  const t = (entryStartTime || '').trim();
  return t ? `Entries open ${dateStr} at ${t}` : `Entries open ${dateStr}`;
}
